import { useEffect, useState } from 'react'
import type { AuthAccountSummaryResponse } from '@ideon-api/sdk-internal'
import { mergedIntoFromError, sdkErrorMessage, useAuthServiceClient } from './sdk-client'

type Summary = NonNullable<AuthAccountSummaryResponse['data']>

export interface AccountDetail {
  summary: AuthAccountSummaryResponse['data']
  // Staff-only fields; a customer-shaped summary omits them.
  permissionSets: NonNullable<Summary['permissionSets']>
  threeScaleApplications: NonNullable<Summary['threeScaleApplications']>
}

export type DetailState =
  | { status: 'loading' }
  // `mergedInto` is the successor slug (PD-30247's 404 shape) when this
  // account's slug was merged away rather than never existing -- see
  // `mergedIntoFromError`.
  | { status: 'error'; message: string; mergedInto?: string }
  | { status: 'ready'; data: AccountDetail }

export function useAccountDetail(slug: string): DetailState {
  const getClient = useAuthServiceClient()
  const [state, setState] = useState<DetailState>({ status: 'loading' })

  useEffect(() => {
    let cancelled = false
    setState({ status: 'loading' })

    getClient()
      .then((client) => client.auth.getAccount(slug))
      .then((summary) => {
        if (!cancelled) {
          setState({
            status: 'ready',
            data: {
              summary: summary.data,
              permissionSets: summary.data?.permissionSets ?? [],
              threeScaleApplications: summary.data?.threeScaleApplications ?? [],
            },
          })
        }
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setState({
            status: 'error',
            message: sdkErrorMessage(error, 'Failed to load account'),
            mergedInto: mergedIntoFromError(error),
          })
        }
      })

    return () => {
      cancelled = true
    }
  }, [getClient, slug])

  return state
}
